import { useState, useRef, useCallback, useEffect } from "react";

type RecordingState = "idle" | "requesting" | "recording" | "stopped";

interface AudioRecordingResult {
  blob: Blob;
  mimeType: string;
  duration: number;
}

export interface UseAudioRecordingOptions {
  maxDuration?: number; // in seconds, default 15 min (900s)
}

export interface UseAudioRecordingReturn {
  state: RecordingState;
  error: string | null;
  duration: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<AudioRecordingResult | null>;
  cancelRecording: () => void;
  analyserNode: AnalyserNode | null;
}

const MAX_DURATION_DEFAULT = 900; // 15 minutes

export function useAudioRecording(
  options: UseAudioRecordingOptions = {},
): UseAudioRecordingReturn {
  const { maxDuration = MAX_DURATION_DEFAULT } = options;

  const [state, setState] = useState<RecordingState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAnalyserNode(null);
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setState("requesting");
      setError(null);
      setDuration(0);

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const isSecureContext = window.isSecureContext;
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;

        let errorMessage = "Audio recording requires a secure context (HTTPS or localhost).";

        if (!isSecureContext && protocol === "http:") {
          if (hostname !== "localhost" && hostname !== "127.0.0.1") {
            errorMessage += ` You're accessing via ${protocol}//${hostname}. Please use localhost or HTTPS.`;
          } else {
            errorMessage = "Your browser doesn't support audio recording (getUserMedia API).";
          }
        }

        throw new Error(errorMessage);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio context and analyser for visualization
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      setAnalyserNode(analyser);

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Determine MIME type (WebM for Chrome/Firefox, MP4 for Safari)
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();
      setState("recording");
      console.log("✅ Recording started successfully");

      // Duration tracking
      durationIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(elapsed);

        if (elapsed >= maxDuration) {
          // Auto-stop at max duration
          mediaRecorderRef.current?.stop();
        }
      }, 1000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to start recording";
      console.error("❌ Recording failed:", message, err);
      setError(message);
      setState("idle");
      cleanup();
    }
  }, [maxDuration, cleanup]);

  const stopRecording = useCallback((): Promise<AudioRecordingResult | null> => {
    console.log("🛑 stopRecording called, state:", state);
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder) {
        resolve(null);
        return;
      }

      if (state !== "recording") {
        resolve(null);
        return;
      }

      console.log("✅ Stopping mediaRecorder...");

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        const recordingDuration = Math.floor(
          (Date.now() - startTimeRef.current) / 1000,
        );

        console.log("✅ Audio processed:", {
          blobSize: blob.size,
          mimeType: mediaRecorder.mimeType,
          duration: recordingDuration,
          chunksCount: chunksRef.current.length,
        });

        cleanup();
        setState("stopped");

        resolve({
          blob,
          mimeType: mediaRecorder.mimeType,
          duration: recordingDuration,
        });
      };

      mediaRecorder.stop();
    });
  }, [state, cleanup]);

  const cancelRecording = useCallback(() => {
    cleanup();
    setState("idle");
    setDuration(0);
    setError(null);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    state,
    error,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
    analyserNode,
  };
}
